import React, { createContext, useContext, useReducer, useEffect, useCallback } from 'react';
import { apiRequest } from '../config/api';
import posthog from '../config/posthog';

const AuthContext = createContext();

const authReducer = (state, action) => {
  switch (action.type) {
    case 'LOGIN_SUCCESS':
      localStorage.setItem('token', action.payload.access_token);
      localStorage.setItem('user', JSON.stringify(action.payload.user));
      if (action.payload.organization) {
        localStorage.setItem('organization', JSON.stringify(action.payload.organization));
      }
      // Identify user in PostHog
      if (posthog.__loaded && action.payload.user) {
        posthog.identify(String(action.payload.user.id), {
          email: action.payload.user.email,
          name: action.payload.user.full_name,
        });
      }
      return {
        ...state,
        isAuthenticated: true,
        user: action.payload.user,
        organization: action.payload.organization,
        token: action.payload.access_token,
        loading: false,
      };
    case 'LOGOUT':
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('organization');
      // Reset PostHog identity
      if (posthog.__loaded) {
        posthog.reset();
      }
      return {
        ...state,
        isAuthenticated: false,
        user: null,
        organization: null,
        token: null,
        loading: false,
      };
    case 'SET_LOADING':
      return {
        ...state,
        loading: action.payload,
      };
    case 'SWITCH_ORGANIZATION':
      localStorage.setItem('organization', JSON.stringify(action.payload));
      return {
        ...state,
        organization: action.payload,
      };
    case 'UPDATE_ORGANIZATION':
      localStorage.setItem('organization', JSON.stringify(action.payload));
      return {
        ...state,
        organization: action.payload,
      };
    case 'UPDATE_USER':
      localStorage.setItem('user', JSON.stringify(action.payload));
      return {
        ...state,
        user: action.payload,
      };
    case 'INIT_AUTH':
      // Re-identify user in PostHog on page refresh
      if (posthog.__loaded && action.payload.token && action.payload.user) {
        posthog.identify(String(action.payload.user.id), {
          email: action.payload.user.email,
          name: action.payload.user.full_name,
        });
      }
      return {
        ...state,
        isAuthenticated: !!action.payload.token,
        user: action.payload.user,
        organization: action.payload.organization,
        token: action.payload.token,
        loading: false,
      };
    default:
      return state;
  }
};

const initialState = {
  isAuthenticated: false,
  user: null,
  organization: null,
  token: null,
  loading: true,
};

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  useEffect(() => {
    // Initialize auth state from localStorage
    const token = localStorage.getItem('token');
    const user = localStorage.getItem('user');
    const organization = localStorage.getItem('organization');

    dispatch({
      type: 'INIT_AUTH',
      payload: {
        token,
        user: user ? JSON.parse(user) : null,
        organization: organization ? JSON.parse(organization) : null,
      },
    });
  }, []);

  const loginWithToken = (tokenData) => {
    dispatch({ type: 'LOGIN_SUCCESS', payload: tokenData });
  };

  const login = async (email, password) => {
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await apiRequest('/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email, password }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Login failed');
      }

      const data = await response.json();
      dispatch({ type: 'LOGIN_SUCCESS', payload: data });
      return data;
    } catch (error) {
      dispatch({ type: 'SET_LOADING', payload: false });
      throw error;
    }
  };

  const register = async (email, username, password, fullName, organizationName, acceptTerms = false, brand = null) => {
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await apiRequest('/auth/register', {
        method: 'POST',
        body: JSON.stringify({
          email,
          username,
          password,
          full_name: fullName,
          organization_name: organizationName,
          accept_terms: acceptTerms,
          brand: brand,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Registration failed');
      }

      const data = await response.json();
      dispatch({ type: 'LOGIN_SUCCESS', payload: data });
      return data;
    } catch (error) {
      dispatch({ type: 'SET_LOADING', payload: false });
      throw error;
    }
  };

  const logout = useCallback(() => {
    dispatch({ type: 'LOGOUT' });
  }, []);

  const switchOrganization = async (organizationId) => {
    try {
      const response = await apiRequest(`/auth/organizations/${organizationId}/switch`, {
        method: 'POST',
      });

      if (!response.ok) {
        throw new Error('Failed to switch organization');
      }

      // Fetch the updated organization info
      const orgResponse = await apiRequest('/organizations/current');

      if (orgResponse.ok) {
        const organization = await orgResponse.json();
        dispatch({ type: 'SWITCH_ORGANIZATION', payload: organization });
      }
    } catch (error) {
      console.error('Error switching organization:', error);
      throw error;
    }
  };

  const updateOrganization = useCallback(async () => {
    try {
      const response = await apiRequest('/organizations/current');
      if (response.ok) {
        const organization = await response.json();
        dispatch({ type: 'UPDATE_ORGANIZATION', payload: organization });
        return organization;
      }
    } catch (error) {
      console.error('Error updating organization:', error);
      throw error;
    }
  }, []); // No dependencies needed

  const setUser = useCallback((user) => {
    dispatch({ type: 'UPDATE_USER', payload: user });
  }, []);

  return (
    <AuthContext.Provider
      value={{
        ...state,
        login,
        loginWithToken,
        register,
        logout,
        switchOrganization,
        updateOrganization,
        setUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
